/** React Query hooks for Expert Strategy Library. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expertApi } from '../services/expertApi';
import type { StrategyCreateRequest, StrategyUpdateRequest } from '../types/strategy';

const STRATEGIES_KEY = ['expert-strategies'];

export function useStrategies(params: {
  type?: string;
  status?: string;
  is_active?: boolean;
} = {}) {
  return useQuery({
    queryKey: [...STRATEGIES_KEY, params],
    queryFn: () => expertApi.getStrategies(params),
  });
}

export function useStrategy(id: number | null, opts?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: [...STRATEGIES_KEY, id],
    queryFn: () => expertApi.getStrategy(id!),
    enabled: id !== null,
    refetchInterval: opts?.refetchInterval,
  });
}

export function useCreateStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StrategyCreateRequest) => expertApi.createStrategy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function useUpdateStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StrategyUpdateRequest }) =>
      expertApi.updateStrategy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function useDeleteStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expertApi.deleteStrategy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function useActivateStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      expertApi.activateStrategy(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function usePromoteStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expertApi.promoteStrategy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function useToggleUserActivation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      expertApi.toggleUserActivation(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function useExtractStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expertApi.extractStrategy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGIES_KEY });
    },
  });
}

export function useSuggestParameters() {
  return useMutation({
    mutationFn: (id: number) => expertApi.suggestParameters(id),
  });
}
